// Admin → API Docs. Dropdown to pick which external API to read:
//  - eBay Message API keeps its bespoke, live-status reference (EbayApiReference).
//  - Post-Order / Fulfillment / Trading / Browse / TRQ render as field-by-field
//    COVERAGE MATRICES (CoverageMatrix + a data module each) — the same
//    Field / Pulling? / DB-column view as the Message API reference.
//  - eBay OAuth is a narrative doc, rendered from docs/ebay-oauth.md (bundled via
//    the @docs alias + ?raw).
import { useEffect, useState } from 'react'
import {
  Box, Stack, Typography, FormControl, InputLabel, Select, MenuItem, Paper,
} from '@mui/material'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { fetchIntegrations } from './api.js'
import EbayApiReference from './EbayApiReference.jsx'
import CoverageMatrix from './apidocs/CoverageMatrix.jsx'
import trqCoverage from './apidocs/trqCoverage.js'
import postOrderCoverage from './apidocs/postOrderCoverage.js'
import tradingCoverage from './apidocs/tradingCoverage.js'
import fulfillmentCoverage from './apidocs/fulfillmentCoverage.js'
import browseCoverage from './apidocs/browseCoverage.js'
import ebayOauthMd from '@docs/ebay-oauth.md?raw'

// writeKey maps each matrix to the integration whose live Write flag governs its
// gated actions (from GET /api/admin/integrations), so "gated off" vs. "live on
// approval" reflects reality. Fulfillment shares Post-Order's write flag; the
// Trading write (member message) is governed by the Message API's write flag.
const DOCS = [
  { key: 'message', label: 'eBay Message API — ingest', kind: 'component' },
  { key: 'post-order', label: 'eBay Post-Order — cancellations / returns / cases / INR', kind: 'matrix', data: postOrderCoverage, writeKey: 'ebay_post_order' },
  { key: 'fulfillment', label: 'eBay Sell Fulfillment — refunds', kind: 'matrix', data: fulfillmentCoverage, writeKey: 'ebay_post_order' },
  { key: 'trading', label: "eBay Trading — member message / int'l check", kind: 'matrix', data: tradingCoverage, writeKey: 'ebay_message' },
  { key: 'browse', label: 'eBay Browse — listing lookup', kind: 'matrix', data: browseCoverage, writeKey: null },
  { key: 'trq', label: 'TRQ — Order Management', kind: 'matrix', data: trqCoverage, writeKey: 'trq' },
  { key: 'oauth', label: 'eBay OAuth — tokens & scopes', kind: 'markdown', md: ebayOauthMd },
]

// Readable rendering for the one remaining bundled-markdown doc (OAuth).
const mdSx = {
  '& h1': { fontSize: '1.4rem', fontWeight: 700, mt: 0, mb: 1.5 },
  '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 3, mb: 1, borderBottom: 1, borderColor: 'divider', pb: 0.5 },
  '& h3': { fontSize: '1rem', fontWeight: 700, mt: 2, mb: 0.5 },
  '& p, & li': { fontSize: '0.9rem', lineHeight: 1.6 },
  '& a': { color: 'primary.main' },
  '& code': { fontFamily: 'monospace', fontSize: '0.82rem', bgcolor: 'action.hover', px: 0.5, py: 0.1, borderRadius: 0.5 },
  '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowX: 'auto' },
  '& pre code': { bgcolor: 'transparent', p: 0 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5, display: 'block', overflowX: 'auto' },
  '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, fontSize: '0.82rem', textAlign: 'left' },
  '& th': { bgcolor: 'action.hover', fontWeight: 700 },
  '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
}

export default function ApiDocs() {
  const [sel, setSel] = useState('message')
  const [integrations, setIntegrations] = useState(null)
  useEffect(() => { fetchIntegrations().then((d) => setIntegrations(d.integrations)) }, [])
  const current = DOCS.find((d) => d.key === sel) ?? DOCS[0]

  // Live Write-flag state for the selected matrix's integration (null = unknown).
  const writeEnabledFor = (key) => {
    if (!key || !integrations) return null
    const cap = integrations.find((i) => i.key === key)?.capabilities?.find((c) => c.kind === 'Write')
    return cap ? cap.state === 'enabled' : null
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>API Docs</Typography>
        <FormControl size="small" sx={{ minWidth: 380 }}>
          <InputLabel id="api-docs-select">API</InputLabel>
          <Select labelId="api-docs-select" value={sel} label="API" onChange={(e) => setSel(e.target.value)}>
            {DOCS.map((d) => (
              <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {current.kind === 'component' && <EbayApiReference />}
      {current.kind === 'matrix' && <CoverageMatrix data={current.data} writeEnabled={writeEnabledFor(current.writeKey)} />}
      {current.kind === 'markdown' && (
        <Paper variant="outlined" sx={{ p: 3, ...mdSx }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.md}</ReactMarkdown>
        </Paper>
      )}
    </Box>
  )
}
